/**
 * <s-hero-carousel> — Auto-advancing fade carousel for the homepage hero.
 *
 * Commands (via commandfor/command on descendant buttons):
 *   --prev — Show the previous slide.
 *   --next — Show the next slide.
 *   --go   — Show the slide whose index is in the source button's data-index.
 *
 * Markup:
 *   [data-slide] — Each slide. The active one gets data-active; others are inert.
 *   [data-dot]   — Optional pager buttons, aria-current mirrors the active slide.
 *
 * Attributes:
 *   interval — Milliseconds between slides (default 5000). Autoplay pauses
 *              while the pointer or keyboard focus is inside the carousel and
 *              is disabled for visitors who prefer reduced motion.
 */
class SHeroCarousel extends HTMLElement {
  #index = 0;
  #timer = null;
  #paused = false;
  #ac;

  connectedCallback() {
    this.#ac = new AbortController();
    const { signal } = this.#ac;
    this.slides = [...this.querySelectorAll('[data-slide]')];
    this.dots = [...this.querySelectorAll('[data-dot]')];
    if (this.slides.length === 0) return;

    this.addEventListener('command', (event) => {
      if (event.command === '--prev') this.show(this.#index - 1);
      if (event.command === '--next') this.show(this.#index + 1);
      if (event.command === '--go') this.show(Number(event.source?.dataset.index || 0));
      this.#restart();
    }, { signal });

    const pause = () => { this.#paused = true; };
    const resume = () => { this.#paused = false; };
    this.addEventListener('pointerenter', pause, { signal });
    this.addEventListener('pointerleave', resume, { signal });
    this.addEventListener('focusin', pause, { signal });
    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) resume();
    }, { signal });

    this.show(0);
    this.#restart();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    clearInterval(this.#timer);
  }

  show(index) {
    const count = this.slides.length;
    this.#index = (index + count) % count;
    this.slides.forEach((slide, i) => {
      const active = i === this.#index;
      slide.toggleAttribute('data-active', active);
      slide.inert = !active;
      slide.setAttribute('aria-hidden', String(!active));
    });
    this.dots.forEach((dot, i) => {
      if (i === this.#index) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
  }

  #restart() {
    clearInterval(this.#timer);
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (this.slides.length < 2) return;
    const interval = Number(this.getAttribute('interval')) || 5000;
    this.#timer = setInterval(() => {
      if (!this.#paused && !document.hidden) this.show(this.#index + 1);
    }, interval);
  }
}

if (!customElements.get('s-hero-carousel')) customElements.define('s-hero-carousel', SHeroCarousel);
